import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { RetentionData } from '../services/youtubeAnalyticsService';

export type TopicVisualizationMode = 'overall' | 'substance' | 'resonance' | 'clarity' | 'production' | 'creative_risk';
export type RetentionMode = 'off' | 'relative' | 'absolute' | 'adjusted' | 'drops';
export type TimelineOverlayMode = 'topics' | 'questions';

const TOPIC_VISUALIZATION_STORAGE_KEYS = {
  mode: 'rescript.topicVisualization.mode',
  showGradient: 'rescript.topicVisualization.showGradient',
  retentionMode: 'rescript.topicVisualization.retentionMode',
  overlayMode: 'rescript.topicVisualization.overlayMode',
} as const;

const VALID_OVERLAY_MODES: TimelineOverlayMode[] = ['topics', 'questions'];

const VALID_TOPIC_VISUALIZATION_MODES: TopicVisualizationMode[] = [
  'overall',
  'substance',
  'resonance',
  'clarity',
  'production',
  'creative_risk',
];

const VALID_RETENTION_MODES: RetentionMode[] = ['off', 'relative', 'absolute', 'adjusted', 'drops'];

const readStoredTopicVisualizationMode = (): TopicVisualizationMode => {
  if (typeof window === 'undefined') {
    return 'overall';
  }

  const savedMode = window.localStorage.getItem(TOPIC_VISUALIZATION_STORAGE_KEYS.mode);
  return VALID_TOPIC_VISUALIZATION_MODES.includes(savedMode as TopicVisualizationMode)
    ? (savedMode as TopicVisualizationMode)
    : 'overall';
};

const readStoredShowGradient = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const savedShowGradient = window.localStorage.getItem(TOPIC_VISUALIZATION_STORAGE_KEYS.showGradient);
  if (savedShowGradient === null) {
    return true;
  }

  try {
    return JSON.parse(savedShowGradient);
  } catch {
    return true;
  }
};

const readStoredOverlayMode = (): TimelineOverlayMode => {
  if (typeof window === 'undefined') {
    return 'topics';
  }

  const savedOverlayMode = window.localStorage.getItem(TOPIC_VISUALIZATION_STORAGE_KEYS.overlayMode);
  return VALID_OVERLAY_MODES.includes(savedOverlayMode as TimelineOverlayMode)
    ? (savedOverlayMode as TimelineOverlayMode)
    : 'topics';
};

const readStoredRetentionMode = (): RetentionMode => {
  if (typeof window === 'undefined') {
    return 'off';
  }

  const savedRetentionMode = window.localStorage.getItem(TOPIC_VISUALIZATION_STORAGE_KEYS.retentionMode);
  return VALID_RETENTION_MODES.includes(savedRetentionMode as RetentionMode)
    ? (savedRetentionMode as RetentionMode)
    : 'off';
};

interface TopicVisualizationContextType {
  mode: TopicVisualizationMode;
  showGradient: boolean;
  setMode: (mode: TopicVisualizationMode) => void;
  setShowGradient: (show: boolean) => void;
  // Retention visualization
  retentionMode: RetentionMode;
  setRetentionMode: (mode: RetentionMode) => void;
  retentionData: RetentionData | null;
  setRetentionData: (data: RetentionData | null) => void;
  // Timeline overlay mode (topics vs questions)
  overlayMode: TimelineOverlayMode;
  setOverlayMode: (mode: TimelineOverlayMode) => void;
}

const TopicVisualizationContext = createContext<TopicVisualizationContextType | undefined>(undefined);

export const useTopicVisualization = () => {
  const context = useContext(TopicVisualizationContext);
  if (!context) {
    throw new Error('useTopicVisualization must be used within a TopicVisualizationProvider');
  }
  return context;
};

interface TopicVisualizationProviderProps {
  children: ReactNode;
}

export const TopicVisualizationProvider: React.FC<TopicVisualizationProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<TopicVisualizationMode>(readStoredTopicVisualizationMode);
  const [showGradient, setShowGradient] = useState(readStoredShowGradient);
  const [retentionMode, setRetentionMode] = useState<RetentionMode>(readStoredRetentionMode);
  const [retentionData, setRetentionData] = useState<RetentionData | null>(null);
  const [overlayMode, setOverlayMode] = useState<TimelineOverlayMode>(readStoredOverlayMode);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(TOPIC_VISUALIZATION_STORAGE_KEYS.mode, mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(
      TOPIC_VISUALIZATION_STORAGE_KEYS.showGradient,
      JSON.stringify(showGradient)
    );
  }, [showGradient]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(TOPIC_VISUALIZATION_STORAGE_KEYS.retentionMode, retentionMode);
  }, [retentionMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(TOPIC_VISUALIZATION_STORAGE_KEYS.overlayMode, overlayMode);
  }, [overlayMode]);

  return (
    <TopicVisualizationContext.Provider value={{
      mode,
      showGradient,
      setMode,
      setShowGradient,
      retentionMode,
      setRetentionMode,
      retentionData,
      setRetentionData,
      overlayMode,
      setOverlayMode
    }}>
      {children}
    </TopicVisualizationContext.Provider>
  );
};

// Helper function to get topic color based on ranking
export const getTopicRankingColor = (ranking: number, showGradient: boolean = true): string => {
  // Normalize ranking to 0-1 scale (assuming ranking is 1-10)
  const ratio = ranking / 10;

  if (showGradient) {
    // Gradient from red to yellow to green
    if (ratio >= 0.8) return 'bg-green-100 dark:bg-green-800 border-green-300 dark:border-green-600 text-green-800 dark:text-green-200';
    if (ratio >= 0.6) return 'bg-lime-100 dark:bg-lime-800 border-lime-300 dark:border-lime-600 text-lime-800 dark:text-lime-200';
    if (ratio >= 0.4) return 'bg-yellow-100 dark:bg-yellow-800 border-yellow-300 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200';
    if (ratio >= 0.2) return 'bg-orange-100 dark:bg-orange-800 border-orange-300 dark:border-orange-600 text-orange-800 dark:text-orange-200';
    return 'bg-red-100 dark:bg-red-800 border-red-300 dark:border-red-600 text-red-800 dark:text-red-200';
  } else {
    // Simple three-color scheme
    if (ratio >= 0.67) return 'bg-green-100 dark:bg-green-800 border-green-300 dark:border-green-600 text-green-800 dark:text-green-200';
    if (ratio >= 0.33) return 'bg-yellow-100 dark:bg-yellow-800 border-yellow-300 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200';
    return 'bg-red-100 dark:bg-red-800 border-red-300 dark:border-red-600 text-red-800 dark:text-red-200';
  }
};

// Helper function to get retention-based color for topics
export const getRetentionColor = (
  ratio: number,
  showGradient: boolean = true,
  isSticky: boolean = false
): string => {
  // Ratio is 0-1 scale for retention
  if (showGradient) {
    // Gradient from red to yellow to green
    if (ratio >= 0.8) {
      return isSticky
        ? 'bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200'
        : 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700/50 text-green-800 dark:text-green-200';
    } else if (ratio >= 0.6) {
      return isSticky
        ? 'bg-lime-100 dark:bg-lime-900 border-lime-300 dark:border-lime-700 text-lime-800 dark:text-lime-200'
        : 'bg-lime-100 dark:bg-lime-900/30 border-lime-300 dark:border-lime-700/50 text-lime-800 dark:text-lime-200';
    } else if (ratio >= 0.4) {
      return isSticky
        ? 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
        : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700/50 text-yellow-800 dark:text-yellow-200';
    } else if (ratio >= 0.2) {
      return isSticky
        ? 'bg-orange-100 dark:bg-orange-900 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200'
        : 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700/50 text-orange-800 dark:text-orange-200';
    } else {
      return isSticky
        ? 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
        : 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50 text-red-800 dark:text-red-200';
    }
  } else {
    // Simple three-color scheme
    if (ratio >= 0.67) {
      return isSticky
        ? 'bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200'
        : 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700/50 text-green-800 dark:text-green-200';
    } else if (ratio >= 0.33) {
      return isSticky
        ? 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
        : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700/50 text-yellow-800 dark:text-yellow-200';
    } else {
      return isSticky
        ? 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
        : 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50 text-red-800 dark:text-red-200';
    }
  }
};
